import { MutationCache, QueryClient, type Mutation } from '@tanstack/react-query';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { toast } from 'sonner';
import { toastError } from '@/lib/toastError';
import { i18n } from '@/lib/i18n';
import { isDevBypassActive } from '@/lib/devBypass';
import { buildSignInRedirect } from '@/components/me/mePortal';
import { hasServerFieldErrors } from '@/lib/forms/applyServerFieldErrors';
import {
  codeToMessage,
  errorStatus,
  errorToastText,
  isNetworkError,
} from '@/lib/translateBackendError';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** Shown as a success toast once the mutation resolves. */
      successMessage?: MessageDescriptor;
      /** Error toast text when the response carries no code or message we can show. */
      errorFallback?: MessageDescriptor;
      /** The caller renders the failure itself (inline alert, form errors): no global toast. */
      silent?: boolean;
    };
  }
}

/** The keys of the surface summaries (`GET /api/v1/cockpit/summary/*`, `endpointKey`). */
const SUMMARY_ROOT = ['cockpit', 'summary'] as const;

/** The Dashboard's minute poll of a summary (`counts_only`), keyed by its normalised params. */
function isCountsPoll(key: readonly unknown[]): boolean {
  const params = key.at(-1);
  return typeof params === 'object' && params !== null && 'counts_only' in params;
}

export const genericErrorMessage = msg({
  id: 'common.error.generic',
  message: 'Something went wrong. Please try again.',
});

const SESSION_EXPIRED_TOAST_ID = 'session-expired';
const NETWORK_ERROR_TOAST_ID = 'network-error';
// Long enough to read the session toast before the page unloads.
const SIGN_IN_REDIRECT_DELAY_MS = 1500;

export interface AppQueryClientOptions {
  /** Navigates to sign-in; replaced in tests. */
  redirectToSignIn?: (url: string) => void;
}

function isCancelled(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: unknown; code?: unknown };
  return e.name === 'CancelError' || e.name === 'AbortError' || e.code === 'ERR_CANCELED';
}

function toastIdsNow(): Set<string | number> {
  return new Set(toast.getHistory().map((t) => t.id));
}

/**
 * The QueryClient of the admin app, with the global mutation feedback layer.
 *
 * A failed mutation toasts `errorToastText(error, i18n, meta.errorFallback)`
 * for 8 s unless the failure is handled elsewhere:
 * - the hook defines `onError`, or sets `meta.silent`;
 * - the response names fields in `body.errors`: the form marks them inline
 *   (`applyServerFieldErrors`), and a toast on top would repeat it;
 * - the caller raised a toast of its own (`mutate(vars, { onError })` and
 *   `try { await mutateAsync() } catch` are invisible to the cache).
 *
 * The last check is a heuristic: any toast that appears between the failure
 * and one macrotask after every caller callback counts as the caller's. Known
 * gaps: two mutations failing in the same tick see each other's toast and the
 * second stays quiet; a caller that awaits before it toasts (a refetch first)
 * toasts after the check, so the user gets two; a caller that reuses a toast
 * id already in the history is not seen. A hook that owns its failure text
 * should say so with `onError` or `meta.silent`, not rely on the timing.
 *
 * A 401 means the session is gone: one toast, then sign-in, whatever the hook
 * does. `meta.successMessage` adds the success toast.
 */
export function createAppQueryClient(options: AppQueryClientOptions = {}): QueryClient {
  const redirectToSignIn =
    options.redirectToSignIn ?? ((url: string) => window.location.assign(url));
  let redirecting = false;

  const handleSessionExpired = () => {
    toast.error(i18n._(codeToMessage.USER_CONTEXT_MISSING), { id: SESSION_EXPIRED_TOAST_ID });
    if (redirecting) return;
    redirecting = true;
    const { pathname, search } = window.location;
    setTimeout(() => redirectToSignIn(buildSignInRedirect(pathname, search)), SIGN_IN_REDIRECT_DELAY_MS);
  };

  // Runs `check` once the mutation has settled as failed and every caller
  // callback had its turn: the cache's onError runs before the hook's own
  // onError/onSettled (which may await a refetch), the observer's mutate
  // callbacks run on the "error" dispatch, and a `mutateAsync` catch block
  // runs in the microtasks after it. The timeout lands after all of them.
  const afterCallers = (mutation: Mutation<unknown, unknown, unknown>, check: () => void) => {
    const unsubscribe = mutationCache.subscribe((event) => {
      if (event.type !== 'updated' || event.mutation !== mutation || event.action.type !== 'error') return;
      unsubscribe();
      setTimeout(check, 0);
    });
  };

  const onError = (
    error: unknown,
    _variables: unknown,
    _onMutateResult: unknown,
    mutation: Mutation<unknown, unknown, unknown>
  ) => {
    if (isCancelled(error)) return;
    if (errorStatus(error) === 401 && !isDevBypassActive()) {
      handleSessionExpired();
      return;
    }
    if (mutation.meta?.silent || mutation.options.onError) return;
    if (hasServerFieldErrors(error)) return;

    const before = toastIdsNow();
    afterCallers(mutation, () => {
      if (toast.getHistory().some((t) => !before.has(t.id))) return;
      if (isNetworkError(error)) {
        toastError(errorToastText(error, i18n, genericErrorMessage), { id: NETWORK_ERROR_TOAST_ID });
        return;
      }
      toastError(errorToastText(error, i18n, mutation.meta?.errorFallback ?? genericErrorMessage));
    });
  };

  const onSuccess = (
    _data: unknown,
    _variables: unknown,
    _context: unknown,
    mutation: Mutation<unknown, unknown, unknown>
  ) => {
    const successMessage = mutation.meta?.successMessage;
    if (successMessage) toast.success(i18n._(successMessage));
    // A summary is never older than the user's own last save (#2193): any write
    // may change one of its blocks, so every summary refetches when on screen
    // and on its next mount otherwise. The counts poll is left out; the full
    // read refreshes it.
    void client.invalidateQueries({ queryKey: SUMMARY_ROOT, predicate: (query) => !isCountsPoll(query.queryKey) });
  };

  const mutationCache: MutationCache = new MutationCache({ onError, onSuccess });

  const client: QueryClient = new QueryClient({
    mutationCache,
    defaultOptions: {
      queries: {
        // Consider data fresh for 5 minutes
        staleTime: 5 * 60 * 1000,
        // Keep data in cache for 10 minutes
        gcTime: 10 * 60 * 1000,
        // Only retry once on failure
        retry: 1,
        // Don't refetch on window focus.
        //
        // App-wide default. Badge and count queries opt in per hook (see
        // hooks/useNavBadges.ts), and the reconciliation money queries do too
        // (see the FRESHNESS POLICY note in
        // hooks/reconciliation/useDebtorAccountsList.ts) because a stale amount
        // an operator acts on is a different class of problem from a stale
        // roster. Anything else on this default is deliberate.
        refetchOnWindowFocus: false,
      },
    },
  });
  return client;
}
